<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRegisterEntryHeader',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits([
    'reload',
    'close',
    'save',
    'registerChanged',
    'deleted',
  ]);

  const props = defineProps({
    ...userComponentProps,
    entry: {
      type: Object,
      required: true,
    },
    selectRegister: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const messages = ref({
    newTransaction: $fn.value.pgettext('Register', 'New entry'),
  });
  const registerInfo = ref(null);
  const ux = ref([]);
  const b_6917646200545441_style = computed(() => ({
    'font-size': '1em',
  }));
  const b_6424600534600827_style = computed(() => ({
    'font-size': '1em',
  }));

  const $data = computed(() => ({
    messages: messages.value,
    registerInfo: registerInfo.value,
    ux: ux.value,
    b_6917646200545441_style: b_6917646200545441_style.value,
    b_6424600534600827_style: b_6424600534600827_style.value,
  }));

  const $$load_registerInfo = () => {
    return {
      selectedRegisterId: props.entry.version.register_id,
      selectedVersionId: props.entry.version.version_id,
    };
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'registerInfo' },
      { name: 'ux' },
      { name: 'b_6917646200545441_style', nodeId: '6917646200545441' },
      { name: 'b_6424600534600827_style', nodeId: '6424600534600827' },
    ],
    loaders: { registerInfo: $$load_registerInfo },
    ux: {},
    dataRefs: {
      messages,
      registerInfo,
      ux,
      b_6917646200545441_style,
      b_6424600534600827_style,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const resourceTitle = computed(() => {
    return !props.selectRegister && props.entry.version.name;
  });
  const resourceKey = computed(() => {
    if (loaded.value) {
      return props.entry.isNew
        ? messages.value.newTransaction + ` - ${props.entry.entry_number}`
        : props.entry.entry_number;
    }
    return null;
  });

  watch(
    () => props.entry,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('entry');
    }
  );
  watch(
    () => props.selectRegister,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('selectRegister');
    }
  );

  const act_8358196963355853_script_0x7j2631r_value_callable = async (
    $event
  ) => {
    return emit('registerChanged', $event.data);
  };
  const act_8358196963355853_script_0x7j2631r_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8358196963355853_script_0x7j2631r_value_callable($event);
      },
    };
  };
  const act_4576710983066270_emit_dr70hthfe_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_2124730035910225_emit_ytv8wdqv9_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_2124730035910225_reloadGrid_rz5k3nuzl_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2124730035910225_request_u1q7v6fmv_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_entry',
      data: {
        id: props.entry.id,
        etag: props.entry.etag,
        locked: true,
      },
    };
  };
  const act_1972474632001399_emit_jyyh9oaum_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_1972474632001399_reloadGrid_zpbwky25d_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_1972474632001399_request_vc046g8nu_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_entry',
      data: {
        id: props.entry.id,
        etag: props.entry.etag,
        locked: false,
      },
    };
  };
  const act_2383909669375364_emit_0u30278r0_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_2383909669375364_reloadGrid_3l2bl6rsb_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2383909669375364_request_u4cmpoewc_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_entry',
      data: {
        id: props.entry.id,
        etag: props.entry.etag,
        void: true,
      },
    };
  };
  const act_2342151935932928_emit_slhaq2sk9_getActionArgs = async ($event) => {
    return {
      event: 'reload',
    };
  };
  const act_2342151935932928_reloadGrid_j5yqaay77_getActionArgs = async (
    $event
  ) => {
    return {
      grid: 'register',
    };
  };
  const act_2342151935932928_request_3906bq9vm_getActionArgs = async (
    $event
  ) => {
    return {
      operation: 'update_register_entry',
      data: {
        id: props.entry.id,
        etag: props.entry.etag,
        void: false,
      },
    };
  };
  const act_2498310591257107_emit_fkavyglck_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
    };
  };

  const act_8358196963355853_script_0x7j2631r = new actionTypes.ScriptAction({
    actionArgs: act_8358196963355853_script_0x7j2631r_getActionArgs,
  });
  const inst_evh_8358196963355853_updateModelValue_kfknabfbu =
    new actionTypes.EventHandlerImpl({
      action: act_8358196963355853_script_0x7j2631r,
      event: 'update:modelValue',
      displayName: 'script',
    });
  const inst_evh_8358196963355853_updateModelValue =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_8358196963355853_updateModelValue_kfknabfbu],
      event: 'update:modelValue',
    });
  const act_4576710983066270_emit_dr70hthfe = new actionTypes.EmitAction({
    actionArgs: act_4576710983066270_emit_dr70hthfe_getActionArgs,
    displayName: 'reload',
  });
  const inst_evh_4576710983066270_clickPreventStop_4vn7hikim =
    new actionTypes.EventHandlerImpl({
      action: act_4576710983066270_emit_dr70hthfe,
      event: 'click.prevent.stop',
      displayName: 'reload',
    });
  const inst_evh_4576710983066270_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_4576710983066270_clickPreventStop_4vn7hikim],
      event: 'click.prevent.stop',
    });
  const act_2124730035910225_emit_ytv8wdqv9 = new actionTypes.EmitAction({
    actionArgs: act_2124730035910225_emit_ytv8wdqv9_getActionArgs,
  });
  const inst_evh_2124730035910225_success_b3cyug2tp =
    new actionTypes.EventHandlerImpl({
      action: act_2124730035910225_emit_ytv8wdqv9,
      event: 'success',
      displayName: 'emit',
    });
  const act_2124730035910225_reloadGrid_rz5k3nuzl =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2124730035910225_reloadGrid_rz5k3nuzl_getActionArgs,
    });
  const inst_evh_2124730035910225_success_mvq5opm5c =
    new actionTypes.EventHandlerImpl({
      action: act_2124730035910225_reloadGrid_rz5k3nuzl,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_2124730035910225_request_u1q7v6fmv = new actionTypes.RequestAction({
    actionArgs: act_2124730035910225_request_u1q7v6fmv_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_2124730035910225_success_b3cyug2tp,
      inst_evh_2124730035910225_success_mvq5opm5c,
    ],
  });
  const inst_evh_2124730035910225_clickPreventStop_ww7awze03 =
    new actionTypes.EventHandlerImpl({
      action: act_2124730035910225_request_u1q7v6fmv,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_2124730035910225_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2124730035910225_clickPreventStop_ww7awze03],
      event: 'click.prevent.stop',
    });
  const act_1972474632001399_emit_jyyh9oaum = new actionTypes.EmitAction({
    actionArgs: act_1972474632001399_emit_jyyh9oaum_getActionArgs,
  });
  const inst_evh_1972474632001399_success_xgpc9ije1 =
    new actionTypes.EventHandlerImpl({
      action: act_1972474632001399_emit_jyyh9oaum,
      event: 'success',
      displayName: 'emit',
    });
  const act_1972474632001399_reloadGrid_zpbwky25d =
    new actionTypes.ReloadGridAction({
      actionArgs: act_1972474632001399_reloadGrid_zpbwky25d_getActionArgs,
    });
  const inst_evh_1972474632001399_success_kcx3x3pm4 =
    new actionTypes.EventHandlerImpl({
      action: act_1972474632001399_reloadGrid_zpbwky25d,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_1972474632001399_request_vc046g8nu = new actionTypes.RequestAction({
    actionArgs: act_1972474632001399_request_vc046g8nu_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_1972474632001399_success_xgpc9ije1,
      inst_evh_1972474632001399_success_kcx3x3pm4,
    ],
  });
  const inst_evh_1972474632001399_clickPreventStop_mojwqdky9 =
    new actionTypes.EventHandlerImpl({
      action: act_1972474632001399_request_vc046g8nu,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_1972474632001399_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1972474632001399_clickPreventStop_mojwqdky9],
      event: 'click.prevent.stop',
    });
  const act_2383909669375364_emit_0u30278r0 = new actionTypes.EmitAction({
    actionArgs: act_2383909669375364_emit_0u30278r0_getActionArgs,
  });
  const inst_evh_2383909669375364_success_qgg3nm0q3 =
    new actionTypes.EventHandlerImpl({
      action: act_2383909669375364_emit_0u30278r0,
      event: 'success',
      displayName: 'emit',
    });
  const act_2383909669375364_reloadGrid_3l2bl6rsb =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2383909669375364_reloadGrid_3l2bl6rsb_getActionArgs,
    });
  const inst_evh_2383909669375364_success_peaiuv8x9 =
    new actionTypes.EventHandlerImpl({
      action: act_2383909669375364_reloadGrid_3l2bl6rsb,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_2383909669375364_request_u4cmpoewc = new actionTypes.RequestAction({
    actionArgs: act_2383909669375364_request_u4cmpoewc_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_2383909669375364_success_qgg3nm0q3,
      inst_evh_2383909669375364_success_peaiuv8x9,
    ],
  });
  const inst_evh_2383909669375364_clickPreventStop_9p9bcqzpl =
    new actionTypes.EventHandlerImpl({
      action: act_2383909669375364_request_u4cmpoewc,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_2383909669375364_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2383909669375364_clickPreventStop_9p9bcqzpl],
      event: 'click.prevent.stop',
    });
  const act_2342151935932928_emit_slhaq2sk9 = new actionTypes.EmitAction({
    actionArgs: act_2342151935932928_emit_slhaq2sk9_getActionArgs,
  });
  const inst_evh_2342151935932928_success_ceq48ix3q =
    new actionTypes.EventHandlerImpl({
      action: act_2342151935932928_emit_slhaq2sk9,
      event: 'success',
      displayName: 'emit',
    });
  const act_2342151935932928_reloadGrid_j5yqaay77 =
    new actionTypes.ReloadGridAction({
      actionArgs: act_2342151935932928_reloadGrid_j5yqaay77_getActionArgs,
    });
  const inst_evh_2342151935932928_success_z9m443i6w =
    new actionTypes.EventHandlerImpl({
      action: act_2342151935932928_reloadGrid_j5yqaay77,
      event: 'success',
      displayName: 'reloadGrid',
    });
  const act_2342151935932928_request_3906bq9vm = new actionTypes.RequestAction({
    actionArgs: act_2342151935932928_request_3906bq9vm_getActionArgs,
    displayName: 'lock',
    events: [
      inst_evh_2342151935932928_success_ceq48ix3q,
      inst_evh_2342151935932928_success_z9m443i6w,
    ],
  });
  const inst_evh_2342151935932928_clickPreventStop_ggtqiq496 =
    new actionTypes.EventHandlerImpl({
      action: act_2342151935932928_request_3906bq9vm,
      event: 'click.prevent.stop',
      displayName: 'lock',
    });
  const inst_evh_2342151935932928_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2342151935932928_clickPreventStop_ggtqiq496],
      event: 'click.prevent.stop',
    });
  const act_2498310591257107_emit_fkavyglck = new actionTypes.EmitAction({
    actionArgs: act_2498310591257107_emit_fkavyglck_getActionArgs,
  });
  const inst_evh_2498310591257107_success_cpo2makeo =
    new actionTypes.EventHandlerImpl({
      action: act_2498310591257107_emit_fkavyglck,
      event: 'success',
      displayName: 'emit',
    });
  const inst_evh_2498310591257107_success = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2498310591257107_success_cpo2makeo],
    event: 'success',
  });

  const actions = {
    inst_evh_8358196963355853_updateModelValue_kfknabfbu,
    inst_evh_8358196963355853_updateModelValue,
    inst_evh_4576710983066270_clickPreventStop_4vn7hikim,
    reload: inst_evh_4576710983066270_clickPreventStop_4vn7hikim,
    inst_evh_4576710983066270_clickPreventStop,
    inst_evh_2124730035910225_success_b3cyug2tp,
    inst_evh_2124730035910225_success_mvq5opm5c,
    inst_evh_2124730035910225_clickPreventStop_ww7awze03,
    lock: inst_evh_2124730035910225_clickPreventStop_ww7awze03,
    inst_evh_2124730035910225_clickPreventStop,
    inst_evh_1972474632001399_success_xgpc9ije1,
    inst_evh_1972474632001399_success_kcx3x3pm4,
    inst_evh_1972474632001399_clickPreventStop_mojwqdky9,
    inst_evh_1972474632001399_clickPreventStop,
    inst_evh_2383909669375364_success_qgg3nm0q3,
    inst_evh_2383909669375364_success_peaiuv8x9,
    inst_evh_2383909669375364_clickPreventStop_9p9bcqzpl,
    inst_evh_2383909669375364_clickPreventStop,
    inst_evh_2342151935932928_success_ceq48ix3q,
    inst_evh_2342151935932928_success_z9m443i6w,
    inst_evh_2342151935932928_clickPreventStop_ggtqiq496,
    inst_evh_2342151935932928_clickPreventStop,
    inst_evh_2498310591257107_success_cpo2makeo,
    inst_evh_2498310591257107_success,
  };

  const evh_8358196963355853_updateModelValue_kfknabfbu = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_8358196963355853_updateModelValue_kfknabfbu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8358196963355853_updateModelValue = async (event, scope) => {
    return await actions.inst_evh_8358196963355853_updateModelValue.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4576710983066270_clickPreventStop_4vn7hikim = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_4576710983066270_clickPreventStop_4vn7hikim.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_4576710983066270_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_4576710983066270_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2124730035910225_success_b3cyug2tp = async (event, scope) => {
    return await actions.inst_evh_2124730035910225_success_b3cyug2tp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2124730035910225_success_mvq5opm5c = async (event, scope) => {
    return await actions.inst_evh_2124730035910225_success_mvq5opm5c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2124730035910225_clickPreventStop_ww7awze03 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2124730035910225_clickPreventStop_ww7awze03.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2124730035910225_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2124730035910225_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1972474632001399_success_xgpc9ije1 = async (event, scope) => {
    return await actions.inst_evh_1972474632001399_success_xgpc9ije1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1972474632001399_success_kcx3x3pm4 = async (event, scope) => {
    return await actions.inst_evh_1972474632001399_success_kcx3x3pm4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1972474632001399_clickPreventStop_mojwqdky9 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1972474632001399_clickPreventStop_mojwqdky9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1972474632001399_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1972474632001399_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2383909669375364_success_qgg3nm0q3 = async (event, scope) => {
    return await actions.inst_evh_2383909669375364_success_qgg3nm0q3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2383909669375364_success_peaiuv8x9 = async (event, scope) => {
    return await actions.inst_evh_2383909669375364_success_peaiuv8x9.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2383909669375364_clickPreventStop_9p9bcqzpl = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2383909669375364_clickPreventStop_9p9bcqzpl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2383909669375364_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2383909669375364_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2342151935932928_success_ceq48ix3q = async (event, scope) => {
    return await actions.inst_evh_2342151935932928_success_ceq48ix3q.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2342151935932928_success_z9m443i6w = async (event, scope) => {
    return await actions.inst_evh_2342151935932928_success_z9m443i6w.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2342151935932928_clickPreventStop_ggtqiq496 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2342151935932928_clickPreventStop_ggtqiq496.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2342151935932928_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2342151935932928_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2498310591257107_success_cpo2makeo = async (event, scope) => {
    return await actions.inst_evh_2498310591257107_success_cpo2makeo.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2498310591257107_success = async (event, scope) => {
    return await actions.inst_evh_2498310591257107_success.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpBox alignItems="center" class="items-spacing w-100" v-if="loaded">
    <ItpBox direction="column">
      <ItpText muted :text="resourceTitle" class="mb-0" v-if="resourceTitle">
      </ItpText>
      <ItpText tag="h5" :text="resourceKey" class="mb-0"> </ItpText>
    </ItpBox>
    <ItpBox direction="column" v-if="!entry.isNew && entry.invoice">
      <ItpText
        muted
        :text="pgettext('RegisterEntryHeader', 'Invoice number')"
        class="mb-0"
      >
      </ItpText>
      <ItpText tag="h4" :text="entry.invoice.invoice_number" class="mb-0">
      </ItpText>
    </ItpBox>
    <UcRegisterSelector
      :companyId="entry.version.company_id"
      :modelValue="registerInfo"
      toggleClass="bg-white"
      v-if="entry.isNew && selectRegister"
      @update:modelValue="evh_8358196963355853_updateModelValue($event, {})"
    >
    </UcRegisterSelector>
    <h6
      :title="pgettext('ResourceStatus', 'Locked')"
      class="my-0 align-self-center"
      v-if="entry.isReadOnly"
    >
      <span
        class="badge bg-secondary d-inline-flex align-items-center justify-content-start px-2"
        :style="b_6917646200545441_style"
      >
        <ItpIcon :icon="$config.lockIcon"> </ItpIcon>
        <span class="ms-1">
          {{ pgettext('ResourceStatus', 'Locked') }}
        </span>
      </span>
    </h6>
    <h6
      :title="pgettext('ResourceStatus', 'Void')"
      class="my-0 align-self-center"
      v-if="entry.void"
    >
      <span
        class="badge bg-warning d-inline-flex align-items-center justify-content-start px-2"
        :style="b_6424600534600827_style"
      >
        <ItpIcon icon="eva:slash-fill"> </ItpIcon>
        <span class="ms-1">
          {{ pgettext('ResourceStatus', 'Void') }}
        </span>
      </span>
    </h6>
    <BButtonGroup class="ms-auto">
      <ItpButton
        icon="fa-sync-alt"
        :tooltip="gettext('Reload')"
        :spinning="actions.reload.isRunning"
        variant="light"
        class="ms-auto"
        v-if="!entry.isNew"
        @click.prevent.stop="evh_4576710983066270_clickPreventStop($event, {})"
      >
      </ItpButton>
      <BDropdown right variant="light" class="border" v-if="!entry.isNew">
        <template #button-content>
          <ItpIcon fa="bars" class="mx-1"> </ItpIcon>
        </template>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Lock')"
          :tooltip="pgettext('BtnLockTooltip', 'Lock')"
          variant="default"
          v-if="!entry.isNew && entry.meta.can_lock"
          @click.prevent.stop="
            evh_2124730035910225_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Unlock')"
          :tooltip="pgettext('BtnLockTooltip', 'Unlock')"
          variant="default"
          v-if="!entry.isNew && entry.meta.can_unlock"
          @click.prevent.stop="
            evh_1972474632001399_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Invalidate')"
          :tooltip="pgettext('BtnLockTooltip', 'Invalidate')"
          variant="default"
          v-if="!entry.isNew && entry.meta.can_invalidate"
          @click.prevent.stop="
            evh_2383909669375364_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <ItpButton
          dropdown-item
          :text="pgettext('BtnLockText', 'Revoke invalidated')"
          :tooltip="pgettext('BtnLockTooltip', 'Revoke invalidated')"
          variant="default"
          v-if="!entry.isNew && entry.meta.can_revoke_invalidation"
          @click.prevent.stop="
            evh_2342151935932928_clickPreventStop($event, {})
          "
        >
        </ItpButton>
        <BtnDeleteResource
          no-icon
          dropdown-item
          :resource="entry"
          :tooltip="pgettext('Register', 'Delete entry')"
          v-if="!entry.isNew && !entry.isReadOnly"
          @success="evh_2498310591257107_success($event, {})"
        >
        </BtnDeleteResource>
      </BDropdown>
    </BButtonGroup>
  </ItpBox>
</template>
